import React, { useMemo } from "react";
import { Download, Eye, Globe, ExternalLink, FileText } from "lucide-react";
import { useStore } from "../store/useStore";
import { useTranslation } from "react-i18next";

export default function Guide() {
  const { language: currentLanguage, setLanguage } = useStore();
  const { i18n } = useTranslation();
  const isRTL = currentLanguage === "ar";

  const content = {
    ar: {
      title: "دليل المنتجات الرسمي",
      subtitle: "يمكنك عرض أو تحميل دليل المنتجات الخاص بجهاز مشروعات الخدمة الوطنية بثلاث لغات مختلفة.",
      downloadText: "تحميل",
      viewText: "مشاهدة",
      viewInNewTab: "فتح في تاب جديد",
      features: {
        quality: "جودة عالية",
        design: "تصميم احترافي",
        readable: "سهل القراءة"
      },
      instructions: "انقر على زر 'مشاهدة' لفتح الدليل في تاب جديد. يمكنك التصفح والقراءة ثم العودة إلى هذه الصفحة.",
      direction: "rtl",
    },
    en: {
      title: "Official Product Guide",
      subtitle: "View or download the National Service Projects Organization Product Guide in three different languages.",
      downloadText: "Download",
      viewText: "View",
      viewInNewTab: "Open in new tab",
      features: {
        quality: "High quality",
        design: "Professional design",
        readable: "Easy to read"
      },
      instructions: "Click the 'View' button to open the guide in a new tab. You can browse and read, then return to this page.",
      direction: "ltr",
    },
    fr: {
      title: "Guide Officiel des Produits",
      subtitle: "Visualisez ou téléchargez la brochure de l'Organisation des Projets de Service National en trois langues différentes.",
      downloadText: "Télécharger",
      viewText: "Voir",
      viewInNewTab: "Ouvrir dans un nouvel onglet",
      features: {
        quality: "Haute qualité",
        design: "Design professionnel",
        readable: "Facile à lire"
      },
      instructions: "Cliquez sur le bouton 'Voir' pour ouvrir le guide dans un nouvel onglet. Vous pouvez parcourir et lire, puis revenir à cette page.",
      direction: "ltr",
    },
  };

  const currentContent = useMemo(
    () => content[currentLanguage] || content.ar,
    [currentLanguage]
  );

  const brochures = [
    {
      id: 1,
      title: currentLanguage === "ar" ? "الدليل العربي" : currentLanguage === "en" ? "Arabic Guide" : "Guide Arabe",
      description: currentLanguage === "ar" 
        ? "النسخة الكاملة باللغة العربية" 
        : currentLanguage === "en" 
        ? "Complete version in Arabic"
        : "Version complète en arabe",
      file: "/pdf/arabic.pdf",
      bg: "from-blue-600 to-blue-800",
      lang: "ar",
      icon: "📘",
      fileSize: "2.4 MB"
    },
    {
      id: 2,
      title: currentLanguage === "ar" ? "الدليل الإنجليزي" : currentLanguage === "en" ? "English Guide" : "Guide Anglais",
      description: currentLanguage === "ar" 
        ? "النسخة الكاملة باللغة الإنجليزية" 
        : currentLanguage === "en" 
        ? "Complete version in English"
        : "Version complète en anglais",
      file: "/pdf/english.pdf",
      bg: "from-purple-600 to-purple-800",
      lang: "en",
      icon: "📗",
      fileSize: "2.1 MB"
    },
    {
      id: 3,
      title: currentLanguage === "ar" ? "الدليل الفرنسي" : currentLanguage === "en" ? "French Guide" : "Guide Français",
      description: currentLanguage === "ar" 
        ? "النسخة الكاملة باللغة الفرنسية" 
        : currentLanguage === "en" 
        ? "Complete version in French"
        : "Version complète en français",
      file: "/pdf/french.pdf",
      bg: "from-green-600 to-green-800",
      lang: "fr",
      icon: "📙",
      fileSize: "2.3 MB"
    },
  ];

  const handleViewPdf = (brochure) => {
    // فتح الـ PDF في تاب جديد
    window.open(brochure.file, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center"
      dir={currentContent.direction}
    >
      <div className="max-w-6xl mx-auto w-full">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center mb-16">
          <div className="inline-flex items-center gap-3 px-6 py-2.5 mb-6 rounded-2xl bg-white/80 backdrop-blur-sm text-blue-700 text-base font-semibold border border-blue-200/60 shadow-sm">
            <Globe className="w-5 h-5" />
            <span>
              {currentLanguage === "ar"
                ? "اختر لغة الدليل"
                : currentLanguage === "en"
                ? "Choose guide language"
                : "Choisissez la langue du guide"}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {currentContent.title}
          </h1>
          <p className="max-w-2xl text-lg text-gray-600 mb-8 leading-relaxed">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Brochures Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {brochures.map((item) => (
            <div
              key={item.id}
              className="group relative flex flex-col rounded-3xl bg-white/80 backdrop-blur-sm shadow-lg hover:shadow-2xl border border-white/60 hover:border-blue-200/80 transition-all duration-300 p-8 hover:-translate-y-2"
            >
              {/* Icon & Badge */}
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.bg} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transform transition-transform duration-300`}
                >
                  <FileText size={28} />
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    item.lang === "ar" ? "bg-blue-100 text-blue-800" :
                    item.lang === "en" ? "bg-purple-100 text-purple-800" :
                    "bg-green-100 text-green-800"
                  }`}>
                    {item.lang.toUpperCase()}
                  </div>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {item.fileSize}
                  </span>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {item.title}
              </h3>
              <p className="text-gray-500 mb-6 leading-relaxed flex-grow">
                {item.description}
              </p>

              {/* Features */}
              <div className="mb-6 space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>{currentContent.features.quality}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>{currentContent.features.design}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>{currentContent.features.readable}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 mt-auto">
                <button
                  onClick={() => handleViewPdf(item)}
                  className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-base font-semibold text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 group/btn"
                >
                  <Eye size={20} />
                  <span>{currentContent.viewText}</span>
                  <ExternalLink size={16} className="opacity-80" />
                </button>
                
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={item.file}
                    download
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900 transition-all duration-200 group/btn"
                  >
                    <Download size={16} />
                    <span>{currentContent.downloadText}</span>
                  </a>
                  
                  
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Instructions Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/60 max-w-2xl mx-auto">
            <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
              <Eye className="w-8 h-8 text-white" />
            </div>
            <div className="text-center sm:text-left rtl:sm:text-right flex-1">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">
                {currentLanguage === "ar" 
                  ? "طريقة المشاهدة" 
                  : currentLanguage === "en" 
                  ? "Viewing Instructions" 
                  : "Instructions de visualisation"}
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {currentContent.instructions}
              </p>
            </div>
          </div>
        </div>

        
      </div>
    </section>
  );
}